import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { CREATE_RESOURCE_CATEGORY } from '../../../gql/mutations/resourceCategoryMutations'
import { ICreateResourceCategoryResponse, IResourceCategory } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'
import { flattenCategoryTree } from '../categoryTreeHelpers'
import { IntlShape, useIntl } from 'react-intl'
import { BACKEND_ERROR, REQUIRED_MARK, RESOURCE_MESSAGES as R_ } from '../resourceCenterMessages'

/** Fabrica: el mensaje necesita `intl`, asi que el esquema no puede ser un const. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object().shape({
    name: Yup.string().required(intl.formatMessage(R_.vNameRequired)),
  })

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  categories: IResourceCategory[]
  // Carpeta padre preseleccionada (al crear subcarpeta desde un nodo del árbol)
  parentId?: string | null
}

export const ModalAddCategory: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  categories,
  parentId = null,
}) => {
  const intl = useIntl()
  const [createCategory, { data, loading, error }] =
    useMutation<ICreateResourceCategoryResponse>(CREATE_RESOURCE_CATEGORY)

  const formik = useFormik({
    initialValues: {
      name: '',
      parentId: parentId || '',
      order: 0,
    },
    enableReinitialize: true,
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (v) => {
      try {
        await createCategory({
          variables: {
            categoryData: {
              name: v.name,
              parentId: v.parentId ? parseId(v.parentId) : null,
              order: v.order,
            },
          },
        })
      } catch (err) {
        console.error('Error creating category:', err)
        toast.error(intl.formatMessage(R_.errCreateFolder))
      }
    },
  })

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(BACKEND_ERROR, {message: error.message}))
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.createResourceCategory.success) {
      onClose()
      refetch()
      toast.success(intl.formatMessage(R_.toastFolderCreated))
      formik.resetForm()
    } else if (
      data.createResourceCategory.errors &&
      data.createResourceCategory.errors.length > 0
    ) {
      toast.error(intl.formatMessage(BACKEND_ERROR, {message: data.createResourceCategory.errors[0].message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  const parentOptions = flattenCategoryTree(categories)
  const selectedParent = parentOptions.find((opt) => opt.value === formik.values.parentId) || null

  return (
    <Modal show={isOpen} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-folder-plus" /> {intl.formatMessage(R_.newFolder)}
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
            {intl.formatMessage(R_.name)}
            <span className="text-danger ms-1">{REQUIRED_MARK}</span>
            </Form.Label>
            <Form.Control
              name="name"
              placeholder={intl.formatMessage(R_.folderNamePlaceholder)}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.name && formik.touched.name)}
              autoFocus
            />
            {formik.errors.name && formik.touched.name && (
              <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{intl.formatMessage(R_.parentFolder)}</Form.Label>
            <Select
              isClearable
              value={selectedParent}
              onChange={(option) => formik.setFieldValue('parentId', option?.value || '')}
              options={parentOptions}
              placeholder={intl.formatMessage(R_.noneRootFolder)}
            />
            <Form.Text className="text-muted">
            {intl.formatMessage(R_.parentHint)}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{intl.formatMessage(R_.displayOrder)}</Form.Label>
            <Form.Control
              type="number"
              name="order"
              value={formik.values.order}
              onChange={formik.handleChange}
              min="0"
            />
            <Form.Text className="text-muted">{intl.formatMessage(R_.orderHint)}</Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
        {intl.formatMessage(R_.cancel)}
        </Button>
        <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {intl.formatMessage(R_.creating)}
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" /> {intl.formatMessage(R_.createFolder)}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
